import { Observable, Subject, from, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import io from 'socket.io-client';
import { v4 as uuidv4 } from 'uuid';

// AG-UI Event Types
export const EventType = {
    RUN_STARTED: 'RUN_STARTED',
    RUN_FINISHED: 'RUN_FINISHED',
    RUN_ERROR: 'RUN_ERROR',
    STEP_STARTED: 'STEP_STARTED',
    STEP_FINISHED: 'STEP_FINISHED',
    TEXT_MESSAGE_START: 'TEXT_MESSAGE_START',
    TEXT_MESSAGE_CONTENT: 'TEXT_MESSAGE_CONTENT',
    TEXT_MESSAGE_END: 'TEXT_MESSAGE_END',
    TOOL_CALL_START: 'TOOL_CALL_START',
    TOOL_CALL_ARGS: 'TOOL_CALL_ARGS',
    TOOL_CALL_END: 'TOOL_CALL_END',
    TOOL_CALL_RESULT: 'TOOL_CALL_RESULT',
    STATE_SNAPSHOT: 'STATE_SNAPSHOT',
    STATE_DELTA: 'STATE_DELTA',
    MESSAGES_SNAPSHOT: 'MESSAGES_SNAPSHOT',
    RAW: 'RAW',
    CUSTOM: 'CUSTOM'
};

// AG-UI Message Roles
export const Role = {
    USER: 'user',
    ASSISTANT: 'assistant',
    SYSTEM: 'system',
    TOOL: 'tool',
    DEVELOPER: 'developer'
};

/**
 * AG-UI compliant Agent Service for frontend
 */
class AgentService {
    constructor() {
        this.socket = null;
        this.eventSubject = new Subject();
        this.conversations = new Map();
        this.activeRuns = new Map();
        this.subscribers = new Set();
        this.isConnected = false;
    }

    /**
     * Initialize connection to the agent backend
     */
    connect() {
        if (this.socket) {
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            // Connect to same host as the page (works locally and on EC2)
            const socketUrl = window.location.origin;
            console.log('Connecting to Socket.IO at:', socketUrl);
            console.log('Using path: /socket.io');
            console.log('Transports: websocket, polling');
            
            this.socket = io(socketUrl, {
                path: '/socket.io',
                transports: ['websocket', 'polling'],
                timeout: 20000,
                forceNew: true,
                reconnection: true,
                reconnectionDelay: 1000,
                reconnectionAttempts: 5
            });

            this.socket.on('connect', () => {
                console.log('Connected to agent backend');
                this.isConnected = true;
                resolve();
            });

            this.socket.on('disconnect', () => {
                console.log('Disconnected from agent backend');
                this.isConnected = false;
            });

            this.socket.on('agent-event', (event) => {
                this.handleAgentEvent(event);
            });

            this.socket.on('connect_error', (error) => {
                console.error('Connection error:', error);
                reject(error);
            });

            // Timeout connection attempt
            setTimeout(() => {
                if (!this.isConnected) {
                    reject(new Error('Connection timeout'));
                }
            }, 10000);
        });
    }

    /**
     * Disconnect from agent backend
     */
    disconnect() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
            this.isConnected = false;
        }
    }

    /**
     * Run agent with AG-UI protocol
     * @param {Object} input - RunAgentInput
     * @returns {Observable} Stream of AG-UI events
     */
    runAgent(input) {
        const { threadId, runId = uuidv4(), messages, context = {}, tools = [] } = input;

        return new Observable(observer => {
            if (!this.isConnected) {
                observer.error(new Error('Agent service not connected'));
                return;
            }

            // Store active run
            this.activeRuns.set(runId, { threadId, observer, status: 'running' });

            // Create event handler for this run
            const eventHandler = (event) => {
                if (event.runId === runId) {
                    observer.next(event);
                    
                    if (event.type === EventType.RUN_FINISHED || event.type === EventType.RUN_ERROR) {
                        this.activeRuns.delete(runId);
                        observer.complete();
                    }
                }
            };

            // Subscribe to events
            const unsubscribe = this.subscribe({ onEvent: eventHandler });

            // Send run request to backend
            this.socket.emit('run-agent', {
                threadId,
                runId,
                messages,
                context,
                tools
            });

            // Cleanup on unsubscribe
            return () => {
                unsubscribe();
                this.activeRuns.delete(runId);
            };
        });
    }

    /**
     * Send chat message using AG-UI protocol
     * @param {string} message - User message
     * @param {string} threadId - Thread/session ID
     * @param {Object} context - Additional context
     * @returns {Observable} Stream of response events
     */
    sendMessage(message, threadId = 'default', context = {}) {
        const userMessage = {
            id: `msg_${Date.now()}`,
            role: Role.USER,
            content: message,
            timestamp: new Date().toISOString()
        };

        const input = {
            threadId,
            messages: [userMessage],
            context,
            tools: ['analyze_entities', 'search_mitre', 'get_threat_intel', 'process_document']
        };

        return this.runAgent(input);
    }

    /**
     * Subscribe to agent events
     * @param {Object} subscriber - Event subscriber
     * @returns {Function} Unsubscribe function
     */
    subscribe(subscriber) {
        this.subscribers.add(subscriber);
        return () => this.subscribers.delete(subscriber);
    }

    /**
     * Handle incoming agent events
     * @param {Object} event - AG-UI event
     */
    handleAgentEvent(event) {
        // Emit to all subscribers
        this.subscribers.forEach(subscriber => {
            if (subscriber.onEvent) {
                subscriber.onEvent(event);
            }
        });

        // Emit to RxJS subject
        this.eventSubject.next(event);
    }

    /**
     * Get conversation history
     * @param {string} threadId - Thread ID
     * @returns {Array} Message history
     */
    getConversationHistory(threadId) {
        return this.conversations.get(threadId) || [];
    }

    /**
     * Clear conversation history
     * @param {string} threadId - Thread ID
     */
    clearConversation(threadId) {
        this.conversations.delete(threadId);
        
        // Abort any active runs for this thread
        for (const [runId, run] of this.activeRuns.entries()) {
            if (run.threadId === threadId) {
                this.abortRun(runId);
            }
        }
    }

    /**
     * Abort a running agent execution
     * @param {string} runId - Run ID to abort
     */
    abortRun(runId) {
        const run = this.activeRuns.get(runId);
        if (run) {
            if (this.socket) {
                this.socket.emit('abort-run', { runId });
            }
            
            run.observer.error(new Error('Run aborted'));
            this.activeRuns.delete(runId);
        }
    }

    /**
     * Get active runs
     * @returns {Array} List of active runs
     */
    getActiveRuns() {
        return Array.from(this.activeRuns.entries()).map(([runId, run]) => ({
            runId,
            threadId: run.threadId,
            status: run.status
        }));
    }

    /**
     * Get event stream as Observable
     * @returns {Observable} Event stream
     */
    getEventStream() {
        return this.eventSubject.asObservable();
    }
}

// Singleton instance
const agentService = new AgentService();

export default agentService;
